/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/index";
import { ProcedureRunner } from "./ProcedureRunner";

type HistoryRun = {
  id: number;
  run_date: string;
  status: string;
  issues: number;
  started_at: string;
  completed_at: string | null;
  completed_by_name: string | null;
  answered: number;
};

type Props = { procedureId: number; name: string; onClose: () => void };

// Past runs of a procedure — for inspections; open one to see every answer.
export const HistoryModal = defineComponent<Props>({
  name: "ProcedureHistoryModal",
  props: ["procedureId", "name", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const runs = ref<HistoryRun[] | null>(null);
    const open = ref<number | null>(null);

    onMounted(async () => {
      runs.value = (await trpc.workday.procedures.history.query({ id: props.procedureId, limit: 90 })) as HistoryRun[];
    });

    return () => (
      <>
        <LemmonModal
          title={t("workday.history.title").replace("{name}", props.name)}
          onClose={props.onClose}
          renderButtons={() => (
            <div class="border-t p-4 text-right">
              <button class="btn btn-neutral" onClick={() => props.onClose()}>
                {t("workday.common.close")}
              </button>
            </div>
          )}
        >
          <div class="p-6">
            {runs.value == null ? (
              <div class="flex justify-center p-6">
                <Loading />
              </div>
            ) : runs.value.length === 0 ? (
              <p class="text-sm text-base-content/60">{t("workday.history.empty")}</p>
            ) : (
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>{t("workday.history.date")}</th>
                    <th>{t("workday.history.status")}</th>
                    <th>{t("workday.history.by")}</th>
                    <th class="text-right">{t("workday.history.issues")}</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.value.map((r) => (
                    <tr key={r.id} class="cursor-pointer hover:bg-base-200/60" onClick={() => (open.value = r.id)}>
                      <td>{new Date(`${r.run_date}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</td>
                      <td>
                        {r.status === "completed" ? (
                          <span class="badge badge-success badge-sm gap-1">
                            {t("workday.status.completed")}{" "}
                            {r.completed_at && new Date(r.completed_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        ) : (
                          <span class="badge badge-warning badge-outline badge-sm">{t("workday.status.in_progress")}</span>
                        )}
                      </td>
                      <td class="text-sm">{r.completed_by_name ?? "—"}</td>
                      <td class={["text-right font-medium", r.issues > 0 ? "text-error" : "text-base-content/40"].join(" ")}>{r.issues}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </LemmonModal>
        {open.value != null && <ProcedureRunner runId={open.value} onClose={() => (open.value = null)} />}
      </>
    );
  },
});
